import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { OrgLayout } from "../../components/Shell";
import { ErrorState, Skeleton } from "../../components/States";
import { BusyButton, PageHeader, fmtNum, fmtUsd, useToast } from "../../components/ui";
import { formatDateTime } from "../../lib/format";
import { saasBillingApi } from "./api.saas";
import "../staff/staff.css";

export function EnterpriseOfferPage() {
  const { org: orgId, offer: offerId } = useParams({ strict: false }) as { org: string; offer: string };
  const toast = useToast();
  const offer = useQuery({
    queryKey: ["enterprise-offer", orgId, offerId],
    queryFn: () => saasBillingApi.enterpriseOffer(orgId, offerId),
    retry: 0,
  });
  const accept = useMutation({
    mutationFn: () => saasBillingApi.acceptEnterpriseOffer(orgId, offerId),
    onSuccess: (result) => {
      void offer.refetch();
      if (result.invoiceUrl) window.location.assign(result.invoiceUrl);
      else toast.push("success", "Offer accepted. Your invoice is being prepared.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const o = offer.data;
  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="Enterprise offer" subtitle={o ? `Prepared for ${o.orgName}` : undefined} />
      {offer.isPending ? <Skeleton /> : offer.isError ? (
        <div className="card section"><ErrorState error={offer.error} retry={() => void offer.refetch()} /></div>
      ) : o ? (
        <div className="enterprise-offer">
          {o.status === "active" && <div className="banner info">Payment received. Enterprise is active.</div>}
          {o.status === "awaiting_payment" && <div className="banner info">Offer accepted. Pay the annual commitment invoice to activate Enterprise.</div>}
          {o.status === "expired" && <div className="banner warn">This offer expired. Ask Backstory for a new offer.</div>}
          <section className="card form-card">
            <div className="section-head">
              <div>
                <h2>Backstory Enterprise</h2>
                <p className="muted">One-year agreement · {new Date(o.periodStart).toLocaleDateString()} to {new Date(o.periodEnd).toLocaleDateString()}</p>
              </div>
              <span className={`badge ${o.status === "active" ? "ok" : o.status === "expired" ? "warn" : "info"}`}>
                {o.status === "awaiting_payment" ? "Awaiting payment" : o.status}
              </span>
            </div>
            <div className="cust-stats cust-quote-stats">
              <OfferValue label="Annual commitment" value={fmtUsd(o.annualCommitUsd)} hint="Paid upfront through Stripe" />
              <OfferValue label="Monthly platform fee" value={fmtUsd(o.monthlyPlatformUsd)} hint="Drawn from the commitment" />
              <OfferValue label="Payment terms" value="7 days" hint={`Billing contact: ${o.contactEmail}`} />
            </div>
            <h3>Included each month</h3>
            <dl className="cust-kv">
              <div className="cust-kv-row"><dt>Stored sessions</dt><dd>{fmtNum(o.sessionsIncluded)} · then {fmtUsd(o.sessionOveragePer1000)} per 1,000</dd></div>
              <div className="cust-kv-row"><dt>Canvas recording</dt><dd>{fmtNum(o.canvasMinutesIncluded)} minutes · then {fmtUsd(o.canvasMinuteOverage)} per minute</dd></div>
              <div className="cust-kv-row"><dt>Live Assist</dt><dd>{fmtNum(o.liveMinutesIncluded)} minutes · then {fmtUsd(o.liveMinuteOverage)} per minute</dd></div>
              <div className="cust-kv-row"><dt>Custom events</dt><dd>{fmtNum(o.customEventsIncluded)} · then {fmtUsd(o.customEventOveragePerMillion)} per million</dd></div>
              <div className="cust-kv-row"><dt>AI credits</dt><dd>{fmtUsd(o.aiCreditsIncludedUsd)} each month</dd></div>
            </dl>
            <p className="muted small">Extra usage is drawn from the annual commitment. Once the commitment is used, extra usage is invoiced monthly and due in 7 days.</p>
            {o.status === "sent" && (
              <div className="enterprise-offer-accept">
                <p>By accepting, you confirm that you are authorized to approve billing for {o.orgName}. Stripe will issue the annual commitment invoice.</p>
                <BusyButton className="btn primary" busy={accept.isPending} busyLabel="Preparing invoice…" onClick={() => accept.mutate()}>
                  Accept and continue to payment
                </BusyButton>
                <span className="muted small">Offer expires {formatDateTime(o.expiresAt)}.</span>
              </div>
            )}
            {o.status === "awaiting_payment" && o.invoiceUrl && (
              <a className="btn primary" href={o.invoiceUrl}>Open Stripe invoice</a>
            )}
          </section>
        </div>
      ) : null}
    </OrgLayout>
  );
}

function OfferValue({ label, value, hint }: { label: string; value: string; hint: string }) {
  return <div className="card cust-stat"><div className="label">{label}</div><div className="value">{value}</div><div className="muted small">{hint}</div></div>;
}
